import PropTypes from 'prop-types';
import {
    Box,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

const EventFilters = ({ selected, handleChange, values, itemsPerPage = 12 }) => {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 960px
    const isMd = useMediaQuery(theme.breakpoints.up('md')); // > 960px

    const [startIndex, setStartIndex] = useState(0);
    const totalItems = values.length;
    const maxStartIndex = Math.max(0, totalItems - itemsPerPage);

    useEffect(() => {
        const selectedIndex = values.indexOf(selected);

        if (selectedIndex === -1 || isMd) {
            return;
        }

        setStartIndex((previousStartIndex) => {
            if (selectedIndex < previousStartIndex) {
                return selectedIndex;
            }

            if (selectedIndex >= previousStartIndex + itemsPerPage) {
                return Math.min(selectedIndex - itemsPerPage + 1, maxStartIndex);
            }

            return previousStartIndex;
        });
    }, [isMd, itemsPerPage, maxStartIndex, selected, values]);

    useEffect(() => {
        if (isMd && startIndex !== 0) {
            setStartIndex(0);
        }
    }, [isMd, startIndex]);

    const handlePrev = () => {
        setStartIndex((prev) => Math.max(0, prev - itemsPerPage));
    };

    const handleNext = () => {
        setStartIndex((prev) => Math.min(maxStartIndex, prev + itemsPerPage));
    };

    const visibleItems = values.slice(startIndex, startIndex + itemsPerPage);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.50',
                borderRadius: 2,
                p: { xs: 1, sm: 1.5 },
                my: 1,
            }}
        >
            {(isXs || isSm) && startIndex > 0 && (
                <IconButton
                    onClick={handlePrev}
                    size="small"
                    sx={{
                        mr: 1,
                        bgcolor: 'grey.200',
                        '&:hover': { bgcolor: 'grey.300' },
                    }}
                >
                    <ArrowLeftIcon />
                </IconButton>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflowX: 'hidden',
                    width: '100%',
                    justifyContent: isMd ? 'space-between' : 'flex-start',
                    transition: 'transform 0.2s ease-out',
                }}
            >
                {visibleItems.map((value) => (
                    <Box
                        key={value}
                        onClick={(event) => handleChange(event, value)}
                        sx={{
                            flex: isMd ? '1 0 auto' : `0 0 ${100 / itemsPerPage}%`,
                            textAlign: 'center',
                            p: { xs: 0.5, sm: 1 },
                            cursor: 'pointer',
                            bgcolor: selected === value ? 'taphaBlue.light' : 'transparent',
                            color: selected === value ? 'taphaBlue.main' : 'dimGray',
                            fontFamily: 'Open Sans',
                            fontWeight: selected === value ? 700 : 600,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            transition: 'background-color 0.16s ease, color 0.16s ease',
                            '&:hover': {
                                bgcolor: selected === value ? 'taphaBlue.light' : 'grey.100',
                            },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {value}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {(isXs || isSm) && startIndex < maxStartIndex && (
                <IconButton
                    onClick={handleNext}
                    size="small"
                    sx={{
                        ml: 1,
                        bgcolor: 'grey.200',
                        '&:hover': { bgcolor: 'grey.300' },
                    }}
                >
                    <ArrowRightIcon />
                </IconButton>
            )}
        </Box>
    );
};

EventFilters.propTypes = {
    selected: PropTypes.string.isRequired,
    handleChange: PropTypes.func.isRequired,
    values: PropTypes.array.isRequired,
    itemsPerPage: PropTypes.number,
};

export default EventFilters;